/**
 * LLM Configuration Presets
 * Pre-configured setups for different use cases and budgets
 *
 * Each preset defines:
 * - reasoningModel: For analysis phases (SERP → DA)
 * - productionModel: For final HTML generation (Content + Images)
 *
 * TOKEN EFFICIENCY INSIGHT (December 2025):
 * Newer models can be CHEAPER despite higher per-token price because they
 * produce significantly fewer tokens for the same task quality:
 * - Claude Opus 4.5: 48-65% fewer tokens for structured output
 * - Gemini Deep Think: ~40% fewer tokens for complex reasoning
 * - A $1.00 task on Sonnet can cost $0.75 on Opus due to efficiency
 *
 * Sources:
 * - ampcode.com: "Opus 4.5 uses up to 65% fewer tokens"
 * - cosmicjs.com: Cost comparison case studies
 * - datastudios.org: Token efficiency benchmarks
 *
 * Anti-fingerprint: Different presets use different providers
 * to create natural variation in writing styles across sites
 */

// Provider-specific model tiers
export const MODEL_TIERS = {
  claude: {
    budget: 'haiku',
    balanced: 'sonnet',
    quality: 'sonnet',
    premium: 'opus',
  },
  openai: {
    budget: 'gpt-4o-mini',
    balanced: 'gpt-4o',
    quality: 'gpt-5',
    premium: 'gpt-5.1',
  },
  gemini: {
    budget: 'flash-lite',
    balanced: 'flash',
    quality: 'pro',
    premium: 'deep-think',
  },
}

// Cost estimates per site (approximate, in USD)
export const COST_ESTIMATES = {
  economique: { min: 0.08, max: 0.25 },
  equilibre: { min: 0.40, max: 1.00 },
  qualite: { min: 1.50, max: 3.00 },
  maximum: { min: 3.00, max: 6.00 },
}

/**
 * Configuration presets
 * Each preset is optimized for a specific price/quality balance
 */
export const PRESETS = {
  economique: {
    id: 'economique',
    name: 'Économique',
    description: 'Coût minimal, qualité correcte',
    badge: 'Budget',
    badgeColor: 'emerald',
    costRange: '~$0.10-0.25/site',
    // Best budget combo: Gemini Flash Lite (super cheap) for reasoning
    // Sonnet for production (good HTML quality)
    recommended: {
      provider: 'claude',
      reasoningModel: 'haiku',
      productionModel: 'sonnet',
    },
    // Alternatives by provider
    alternatives: {
      claude: { reasoning: 'haiku', production: 'sonnet' },
      openai: { reasoning: 'gpt-4o-mini', production: 'gpt-4o' },
      gemini: { reasoning: 'flash-lite', production: 'flash' },
    },
    warnings: [
      'Analyse moins approfondie',
      'OK pour sites simples',
    ],
  },

  equilibre: {
    id: 'equilibre',
    name: 'Équilibré',
    description: 'Meilleur rapport qualité/prix',
    badge: 'Recommandé',
    badgeColor: 'primary',
    costRange: '~$0.50-1.00/site',
    recommended: {
      provider: 'claude',
      reasoningModel: 'sonnet',
      productionModel: 'sonnet',
    },
    alternatives: {
      claude: { reasoning: 'sonnet', production: 'sonnet' },
      openai: { reasoning: 'gpt-4o', production: 'gpt-4o' },
      gemini: { reasoning: 'flash', production: 'pro' },
    },
    warnings: [],
  },

  qualite: {
    id: 'qualite',
    name: 'Qualité',
    description: 'Analyse poussée, HTML premium',
    badge: 'Pro',
    badgeColor: 'amber',
    costRange: '~$1.50-3.00/site',
    // Note: Opus peut coûter MOINS cher que Sonnet grâce à 48-65% moins de tokens
    efficiencyNote: 'Opus génère ~50% moins de tokens = coût réel souvent < Sonnet',
    recommended: {
      provider: 'claude',
      reasoningModel: 'sonnet',
      productionModel: 'opus',
    },
    alternatives: {
      claude: { reasoning: 'sonnet', production: 'opus' },
      openai: { reasoning: 'gpt-5', production: 'gpt-5.1' },
      gemini: { reasoning: 'pro', production: 'deep-think' },
    },
    warnings: [],
  },

  maximum: {
    id: 'maximum',
    name: 'Maximum',
    description: 'Meilleurs modèles partout',
    badge: 'Premium',
    badgeColor: 'purple',
    costRange: '~$3.00-6.00/site',
    // Note: Malgré prix/token plus élevé, coût réel modéré grâce à l'efficacité
    efficiencyNote: 'Opus partout: prix/token élevé mais tokens générés réduits de 50%+',
    recommended: {
      provider: 'claude',
      reasoningModel: 'opus',
      productionModel: 'opus',
    },
    alternatives: {
      claude: { reasoning: 'opus', production: 'opus' },
      openai: { reasoning: 'gpt-5.1', production: 'gpt-5.1' },
      gemini: { reasoning: 'deep-think', production: 'deep-think' },
    },
    warnings: [
      'Coût élevé',
      'Opus partout = overkill pour analyse',
    ],
  },
}

/**
 * Cross-provider anti-fingerprint configurations
 * Mix providers for reasoning vs production to create unique site signatures
 */
export const ANTI_FINGERPRINT_COMBOS = [
  {
    id: 'claude-openai',
    name: 'Claude → OpenAI',
    reasoning: { provider: 'claude', model: 'sonnet' },
    production: { provider: 'openai', model: 'gpt-4o' },
  },
  {
    id: 'claude-gemini',
    name: 'Claude → Gemini',
    reasoning: { provider: 'claude', model: 'sonnet' },
    production: { provider: 'gemini', model: 'pro' },
  },
  {
    id: 'openai-claude',
    name: 'OpenAI → Claude',
    reasoning: { provider: 'openai', model: 'gpt-4o' },
    production: { provider: 'claude', model: 'sonnet' },
  },
  {
    id: 'openai-gemini',
    name: 'OpenAI → Gemini',
    reasoning: { provider: 'openai', model: 'gpt-4o' },
    production: { provider: 'gemini', model: 'pro' },
  },
  {
    id: 'gemini-claude',
    name: 'Gemini → Claude',
    reasoning: { provider: 'gemini', model: 'flash' },
    production: { provider: 'claude', model: 'sonnet' },
  },
  {
    id: 'gemini-openai',
    name: 'Gemini → OpenAI',
    reasoning: { provider: 'gemini', model: 'flash' },
    production: { provider: 'openai', model: 'gpt-4o' },
  },
]

/**
 * Validate a configuration
 * @returns {{ valid: boolean, warnings: string[], errors: string[] }}
 */
export function validateConfig(config) {
  const warnings = []
  const errors = []

  // Check for problematic production models
  const lowQualityProduction = ['haiku', 'gpt-4o-mini', 'flash-lite']
  if (lowQualityProduction.includes(config.productionModel)) {
    warnings.push(`${config.productionModel} en production peut donner du HTML de qualité inférieure`)
  }

  // Check for overkill reasoning
  const premiumModels = ['opus', 'gpt-5.1', 'deep-think']
  if (premiumModels.includes(config.reasoningModel)) {
    warnings.push(`${config.reasoningModel} pour le raisonnement est coûteux, Sonnet/GPT-4o suffit`)
  }

  return {
    valid: errors.length === 0,
    warnings,
    errors,
  }
}

/**
 * Get preset configuration for a provider
 * @param {string} presetId - Preset ID (economique, equilibre, qualite, maximum)
 * @param {string} provider - Provider (claude, openai, gemini)
 */
export function getPresetConfig(presetId, provider) {
  const preset = PRESETS[presetId]
  if (!preset) return null

  const alt = preset.alternatives[provider]
  if (!alt) return preset.recommended

  return {
    provider,
    reasoningModel: alt.reasoning,
    productionModel: alt.production,
  }
}

/**
 * Get a random anti-fingerprint configuration
 * @param {string[]} availableProviders - List of configured providers
 */
export function getRandomAntiFingerprint(availableProviders) {
  if (availableProviders.length < 2) return null

  const validCombos = ANTI_FINGERPRINT_COMBOS.filter(
    combo =>
      availableProviders.includes(combo.reasoning.provider) &&
      availableProviders.includes(combo.production.provider)
  )

  if (validCombos.length === 0) return null

  return validCombos[Math.floor(Math.random() * validCombos.length)]
}

export default {
  PRESETS,
  MODEL_TIERS,
  COST_ESTIMATES,
  ANTI_FINGERPRINT_COMBOS,
  validateConfig,
  getPresetConfig,
  getRandomAntiFingerprint,
}
